/**
 * useKundeRows — TanStack Query hook for the T1 Kunden grid (Story V10-J;
 * UI-U3 keyset pagination).
 *
 * live mode → GET /customers via the typed client (mapped to KundeRow), one page
 *             at a time; "Mehr laden" appends the next keyset page.
 * mock mode → resolves the existing entity-detail mock (buildKundeRows) as a
 *             single synchronous page (mock layer NOT deleted; deterministic tests).
 *
 * `data` stays a flat `KundeRow[]` so existing consumers (CustomersScreen, the
 * policies-overlay in usePolicyRows) are unaffected.
 *
 * [Source: apps/web/src/features/customers/api/customers-api.ts]
 * [Source: architecture/frontend-architecture.md#3-server-state-tanstack-query-v5]
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchKundeRows } from '../api/customers-api';
import type { CustomerListParams } from '../../../lib/api-client';
import { buildKundeRows } from '../../entity-detail/mock';
import type { KundeRow } from '../../entity-detail/types';

type KundePage = { items: KundeRow[]; nextCursor: string | null };

export function useKundeRows(filters: CustomerListParams = {}) {
  const live = isLiveData();

  const query = useInfiniteQuery<KundePage>({
    queryKey: qk.customers({ ...filters, mode: live ? 'live' : 'mock' }),
    queryFn: ({ pageParam }) =>
      live
        ? fetchKundeRows({ ...filters, cursor: pageParam as string | undefined })
        : Promise.resolve<KundePage>({ items: buildKundeRows(), nextCursor: null }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    ...(live
      ? {}
      : {
          initialData: {
            pages: [{ items: buildKundeRows(), nextCursor: null }],
            pageParams: [undefined],
          },
          staleTime: Infinity,
        }),
  });

  const data: KundeRow[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
